import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button, DataTable, DetailList, Dialog, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import { UnsavedChangesGuard } from "../../../app/forms/UnsavedChangesGuard";
import { failure } from "../../workspace/behavior";
import type { Schemas } from "../../workspace/api.generated";
import type { Command } from "../../workspace/model";
import { date } from "../../workspace/model";
import {
  CommandDialog,
  PageHeader,
  Panel,
  RowAction,
  RowLink,
  StateNotice,
  statusBadge,
} from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import {
  inquiryReplyFormSchema,
  inquirySearchFormSchema,
} from "../schema";
import type {
  InquiryReplyFormValues,
  InquirySearchFormValues,
} from "../schema";

type InquiryListState = "ready" | "loading" | "error" | "denied" | "not-found";

export interface InquiryAttachmentPreview {
  readonly url: string;
  readonly contentType: string;
}

export interface InquiryViewProps {
  readonly mode?: "combined" | "list" | "detail";
  readonly filters: InquirySearchFormValues;
  readonly result?: Schemas["AdminPageInquiryView"];
  readonly selected?: Schemas["InquiryView"];
  readonly state?: InquiryListState;
  readonly actorId: number;
  readonly canManage: boolean;
  readonly unresolvedInquiryId?: number;
  readonly refreshing?: boolean;
  readonly notice?: string;
  readonly recoveryError?: string;
  readonly onSearch: (filters: InquirySearchFormValues) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelect: (inquiry: Schemas["InquiryView"]) => void;
  readonly onRetry: () => void;
  readonly onRefresh: () => Promise<Schemas["InquiryView"] | undefined>;
  readonly onCommand: (command: Command) => Promise<unknown>;
  readonly onCommandComplete: (inquiry: Schemas["InquiryView"]) => Promise<void>;
  readonly onDownloadAttachment: (fileAssetId: number) => Promise<InquiryAttachmentPreview>;
  readonly onReleasePreview: (url: string) => void;
  readonly detailHref?: (inquiry: Schemas["InquiryView"]) => string;
  readonly onOpenDetail?: (inquiry: Schemas["InquiryView"]) => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly onBack?: () => void;
  readonly memberHref?: string;
  readonly memberAccessDenied?: boolean;
  readonly onOpenMember?: () => void;
}

const listColumns: readonly DataColumn<Schemas["InquiryView"]>[] = [
  { key: "number", title: "문의번호", render: (row) => row.inquiryNo },
  { key: "member", title: "회원 ID", numeric: true, render: (row) => row.memberId },
  { key: "title", title: "제목", render: (row) => row.title },
  { key: "status", title: "상태", render: (row) => statusBadge(row.status) },
  { key: "owner", title: "담당자 ID", render: (row) => row.assignedAdminLoginId ?? (row.assignedAdminId ? "확인 불가" : "미배정") },
  { key: "created", title: "접수일시", render: (row) => date(row.createdAt) },
];

export function InquiryView({
  mode = "combined",
  filters,
  result,
  selected,
  state = "ready",
  actorId,
  canManage,
  unresolvedInquiryId,
  refreshing = false,
  notice,
  recoveryError,
  onSearch,
  onPageChange,
  onSelect,
  onRetry,
  onRefresh,
  onCommand,
  onCommandComplete,
  onDownloadAttachment,
  onReleasePreview,
  detailHref,
  onOpenDetail,
  backHref,
  backLabel,
  onBack,
}: InquiryViewProps) {
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<Schemas["InquiryView"]>();
  const [uncertain, setUncertain] = useState(selected?.id === unresolvedInquiryId);
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [downloadingFileId, setDownloadingFileId] = useState<number>();
  const [previewFileId, setPreviewFileId] = useState<number>();
  const [preview, setPreview] = useState<InquiryAttachmentPreview>();
  const previewRef = useRef<InquiryAttachmentPreview | undefined>(undefined);
  const searchForm = useForm<InquirySearchFormValues>({
    resolver: zodResolver(inquirySearchFormSchema),
    values: filters,
    shouldFocusError: true,
  });
  const replyForm = useForm<InquiryReplyFormValues>({
    resolver: zodResolver(inquiryReplyFormSchema),
    defaultValues: { content: "" },
    shouldFocusError: true,
  });

  const releasePreview = () => {
    if (previewRef.current) onReleasePreview(previewRef.current.url);
    previewRef.current = undefined;
    setPreview(undefined);
    setPreviewFileId(undefined);
  };

  useEffect(() => {
    setAssignmentOpen(false);
    setPendingSelection(undefined);
    setReplyError("");
    setReplying(false);
    replyForm.reset({ content: "" });
    if (previewRef.current) onReleasePreview(previewRef.current.url);
    previewRef.current = undefined;
    setPreview(undefined);
    setPreviewFileId(undefined);
  }, [onReleasePreview, replyForm, selected?.id]);

  useEffect(() => {
    setUncertain(selected?.id === unresolvedInquiryId);
  }, [selected?.id, unresolvedInquiryId]);

  useEffect(() => () => {
    if (previewRef.current) onReleasePreview(previewRef.current.url);
  }, [onReleasePreview]);

  const assignedToActor = selected?.assignedAdminId === actorId;
  const assignedToOther = Boolean(selected?.assignedAdminId && !assignedToActor);
  const assignmentAllowed = Boolean(
    selected
    && !selected.assignedAdminId
    && ["RECEIVED", "IN_PROGRESS"].includes(selected.status)
    && canManage
    && !uncertain,
  );
  const replyAllowed = Boolean(
    selected
    && assignedToActor
    && selected.status !== "CLOSED"
    && canManage
    && !uncertain,
  );
  const replyBlockReason = !canManage
    ? "문의 처리 권한이 없습니다. 권한 관리자에게 요청해 주세요."
    : uncertain ? "이전 요청 결과가 확정되지 않았습니다. 최신 담당자와 답변 상태를 조회해 주세요."
      : selected?.status === "CLOSED" ? "종료된 문의에는 답변을 추가할 수 없습니다. 문의 상태를 확인해 주세요."
        : assignedToOther ? "다른 관리자가 담당 중입니다. 담당자가 답변하거나 배정 상태를 확인해 주세요."
          : "먼저 ‘내가 담당하기’를 눌러 문의를 배정받아 주세요.";

  const execute = async (command: Command) => {
    setUncertain(true);
    try {
      return await onCommand(command);
    } catch (cause) {
      if (!failure(cause).reconcile) setUncertain(false);
      throw cause;
    }
  };

  const complete = async (result: unknown) => {
    await onCommandComplete(result as Schemas["InquiryView"]);
    setUncertain(false);
    setReplyError("");
  };

  const refreshLatest = async () => {
    setReplyError("");
    try {
      const latest = await onRefresh();
      setUncertain(false);
      return latest;
    } catch (cause) {
      setReplyError(failure(cause).message);
      throw cause;
    }
  };

  const requestSelection = (inquiry: Schemas["InquiryView"]) => {
    if (inquiry.id === selected?.id) return;
    const hasDraft = replyForm.formState.isDirty && Boolean(replyForm.getValues("content").trim());
    if (hasDraft) {
      setPendingSelection(inquiry);
      return;
    }
    replyForm.reset({ content: "" });
    onSelect(inquiry);
  };

  const submitReply = replyForm.handleSubmit(async ({ content }) => {
    if (!selected || !replyAllowed || replying) return;
    setReplying(true);
    setReplyError("");
    try {
      const updated = await execute({
        operation: "POST /api/v1/admin/inquiries/{inquiryId}/replies",
        params: { inquiryId: selected.id },
        body: { content: content.trim() },
      });
      await complete(updated);
      replyForm.reset({ content: "" });
    } catch (cause) {
      setReplyError(failure(cause).message);
    } finally {
      setReplying(false);
    }
  });

  const openAttachment = async (fileAssetId: number) => {
    if (downloadingFileId !== undefined) return;
    setDownloadingFileId(fileAssetId);
    setReplyError("");
    try {
      releasePreview();
      const downloaded = await onDownloadAttachment(fileAssetId);
      previewRef.current = downloaded;
      setPreview(downloaded);
      setPreviewFileId(fileAssetId);
    } catch (cause) {
      setReplyError(failure(cause).message);
    } finally {
      setDownloadingFileId(undefined);
    }
  };

  const columns = [
    {
      key: "number",
      title: "문의번호",
      sticky: "start" as const,
      render: (row: Schemas["InquiryView"]) => detailHref ? (
        <RowLink
          triggerKey={`inquiry:${row.id}:detail`}
          href={detailHref(row)}
          onNavigate={onOpenDetail ? () => onOpenDetail(row) : undefined}
        >
          {row.inquiryNo}
        </RowLink>
      ) : row.inquiryNo,
    },
    ...listColumns.slice(1),
    ...(mode === "combined" ? [{
      key: "action",
      title: "작업",
      render: (row: Schemas["InquiryView"]) => (
        <RowAction onClick={() => requestSelection(row)}>문의 선택</RowAction>
      ),
    }] : []),
  ] satisfies readonly DataColumn<Schemas["InquiryView"]>[];

  const attachmentColumns = [
    { key: "order", title: "순서", numeric: true, render: (fileId: number) => (selected?.attachmentIds.indexOf(fileId) ?? 0) + 1 },
    { key: "id", title: "파일 ID", numeric: true, render: (fileId: number) => fileId },
    {
      key: "action",
      title: "작업",
      render: (fileId: number) => (
        <Button
          size="small"
          variant="quiet"
          disabled={downloadingFileId !== undefined}
          isLoading={downloadingFileId === fileId}
          onClick={() => { void openAttachment(fileId); }}
        >
          첨부 보기
        </Button>
      ),
    },
  ] satisfies readonly DataColumn<number>[];

  return (
    <>
      <PageHeader
        id={mode === "detail" ? "ADM-S25" : "ADM-S13"}
        title={mode === "detail" ? selected?.inquiryNo ?? "문의 상세" : "1:1 문의 관리"}
        description={mode === "detail" ? "문의 본문·첨부·답변 이력을 확인하고 담당 배정과 답변을 처리합니다." : "문의별 담당자·본문·첨부·답변 이력을 확인하고 배정된 담당자가 답변합니다."}
        breadcrumbs={mode === "detail" ? [{ label: "1:1 문의 관리", href: "/inquiries" }, { label: "문의 상세", href: selected ? detailHref?.(selected) ?? "#" : "#" }] : undefined}
        backAction={mode === "detail" && backHref && onBack ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
        meta={mode !== "detail" && result && state === "ready" ? <p aria-live="polite">총 {result.totalCount.toLocaleString("ko-KR")}건{refreshing ? " · 최신 목록 확인 중" : ""}</p> : undefined}
        action={mode !== "list" && selected ? <Button size="small" variant="quiet" onClick={() => { void refreshLatest().catch(() => undefined); }}>최신 상태 조회</Button> : undefined}
      />
      {notice ? <Alert persistent variant="information" title="문의 처리 결과">{notice}</Alert> : null}
      {unresolvedInquiryId ? <StateNotice state="conflict" onRefresh={() => { void refreshLatest().catch(() => undefined); }} /> : null}
      {recoveryError ? <Alert persistent variant="warning" title="처리 결과 미확정">{recoveryError}</Alert> : null}
      {replyError ? <Alert persistent variant="danger" title="문의 처리에 실패했습니다">{replyError}</Alert> : null}
      {mode === "detail" && !selected && state === "loading" ? <p role="status">문의 상세를 불러오는 중입니다.</p> : null}
      {mode === "detail" && !selected && state === "denied" ? <StateNotice state="denied" /> : null}
      {mode === "detail" && !selected && state === "not-found" ? <StateNotice state="not-found" /> : null}
      {mode === "detail" && !selected && state === "error" ? <Alert persistent variant="danger" title="문의 상세를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}>현재 주소를 유지한 채 다시 조회할 수 있습니다.</Alert> : null}
      {mode !== "detail" ? <><Panel title="검색 조건" description="상태·회원 ID·담당자 ID·접수 기간을 함께 적용할 수 있습니다.">
        <form
          className="admin-filter-bar"
          noValidate
          onSubmit={searchForm.handleSubmit((values) => onSearch({
            ...values,
            memberId: values.memberId.trim(),
            assignedAdminLoginId: values.assignedAdminLoginId.trim(),
          }))}
        >
          <HookFormField compact control="select" formControl={searchForm.control} label="문의 상태" name="status">
            <option value="">전체</option>
            <option value="RECEIVED">접수됨</option>
            <option value="IN_PROGRESS">답변 준비 중</option>
            <option value="ANSWERED">답변 완료</option>
            <option value="CLOSED">종료</option>
          </HookFormField>
          <HookFormField compact formControl={searchForm.control} inputMode="numeric" label="회원 ID" name="memberId" placeholder="숫자 ID" />
          <HookFormField compact formControl={searchForm.control} label="담당자 ID" name="assignedAdminLoginId" placeholder="로그인 ID" />
          <HookFormField compact formControl={searchForm.control} label="접수 시작일" name="from" type="date" />
          <HookFormField compact formControl={searchForm.control} label="접수 종료일" name="to" type="date" />
          <Button size="small" type="submit">조회</Button>
        </form>
      </Panel>
      <div>
        <DataTable
          caption="문의 검색 결과"
          columns={columns}
          rows={state === "ready" ? result?.items ?? [] : []}
          rowKey={(row) => row.id}
          state={state === "not-found" || state === "denied" ? "error" : state}
          onRetry={onRetry}
        />
        {result && state === "ready" ? (
          <Pagination page={result.page} size={result.size} totalCount={result.totalCount} onPageChange={onPageChange} />
        ) : null}
      </div>
      </> : null}
      {mode === "combined" && !selected && state === "ready" ? (
        <Alert persistent variant="information" title="선택한 문의가 없습니다">
          {result?.items.length === 0 ? "조회된 문의가 없습니다. 검색 조건을 변경해 주세요." : "목록에서 확인할 문의를 선택해 주세요."}
        </Alert>
      ) : null}
      {mode !== "list" && selected ? (
        <>
          <div className="admin-split">
            <Panel title="문의 내용" description={selected.inquiryNo}>
              <DetailList items={[
                { label: "상태", value: statusBadge(selected.status) },
                { label: "접수일", value: date(selected.createdAt) },
                { label: "회원명", value: selected.memberName ?? "—" },
                { label: "휴대폰번호", value: selected.phoneNumber ?? "—" },
                { label: "제목", value: selected.title },
                { label: "상세 내용", value: selected.content },
                { label: "담당자 ID", value: selected.assignedAdminLoginId ?? (selected.assignedAdminId ? "확인 불가" : "미배정") },
                { label: "최종 답변 일시", value: date(selected.answeredAt) },
              ]} />
            </Panel>
            <Panel
              title="담당 및 답변"
              description="담당자로 배정된 관리자만 답변을 등록할 수 있습니다."
              action={(
                <GuidedActionButton
                  size="small"
                  variant="secondary"
                  blockedReason={assignmentAllowed ? null : !canManage ? "문의 처리 권한이 없습니다. 권한 관리자에게 요청해 주세요." : uncertain ? "직전 요청 결과가 확정되지 않았습니다. 최신 상태를 조회해 주세요." : assignedToActor ? "이미 내가 담당 중인 문의입니다. 아래에서 답변을 작성해 주세요." : assignedToOther ? "다른 관리자가 담당 중입니다. 담당자 변경은 권한 관리자에게 요청해 주세요." : "현재 문의 상태에서는 담당자로 배정할 수 없습니다."}
                  onClick={() => setAssignmentOpen(true)}
                >
                  {assignedToActor ? "내가 담당 중" : assignedToOther ? `관리자 ${selected.assignedAdminLoginId ?? "확인 불가"} 담당` : "내가 담당하기"}
                </GuidedActionButton>
              )}
            >
              {!canManage ? <p>문의 처리 권한이 없습니다.</p> : null}
              {assignedToOther ? (
                <Alert persistent variant="warning" title="다른 관리자가 담당 중입니다">
                  담당 관리자 ID {selected.assignedAdminLoginId ?? "확인 불가"}의 답변을 기다리거나 최신 배정 상태를 확인해 주세요.
                </Alert>
              ) : null}
              {selected.status === "CLOSED" ? <p>종료된 문의에는 답변을 추가할 수 없습니다.</p> : null}
              {uncertain ? <p>다시 처리하기 전에 최신 담당자와 답변 상태를 조회해 주세요.</p> : null}
              <form className="admin-command" noValidate onSubmit={(event) => { void submitReply(event); }}>
                <HookFormField
                  compact
                  control="textarea"
                  description={!selected.assignedAdminId ? "먼저 이 문의를 담당하세요." : assignedToOther ? "현재 담당 관리자만 답변할 수 있습니다." : undefined}
                  disabled={!replyAllowed || replying}
                  formControl={replyForm.control}
                  label="답변"
                  maxLength={4_000}
                  name="content"
                />
                <GuidedActionButton size="small" blockedReason={replyAllowed ? null : replyBlockReason} disabled={replying} isLoading={replying} type="submit">답변 저장</GuidedActionButton>
              </form>
            </Panel>
          </div>
          <Panel title="첨부" description="문의에 연결된 파일만 INQUIRY context로 확인합니다.">
            {selected.attachmentIds.length > 0 ? (
              <DataTable caption="문의 첨부 파일" columns={attachmentColumns} rows={selected.attachmentIds} rowKey={(fileId) => fileId} />
            ) : <p>첨부 파일이 없습니다.</p>}
          </Panel>
          <Panel title="답변 이력" description={`총 ${selected.replies.length.toLocaleString("ko-KR")}건`}>
            {selected.replies.length > 0 ? (
              <div aria-live="polite" className="admin-stack">
                {selected.replies.map((reply) => (
                  <Alert key={reply.id} persistent variant="information" title={`관리자 ${reply.adminLoginId ?? "확인 불가"} 답변 · ${date(reply.createdAt)}`}>
                    {reply.content}
                  </Alert>
                ))}
              </div>
            ) : <p>등록된 답변이 없습니다.</p>}
          </Panel>
          <CommandDialog
            open={assignmentOpen}
            onClose={() => setAssignmentOpen(false)}
            title="이 문의를 담당할까요?"
            description="배정 후 이 관리자 계정으로 답변을 작성할 수 있습니다."
            operation="문의 담당 배정"
            target={`${selected.inquiryNo} · 문의 ID ${selected.id}`}
            confirmLabel="담당하기"
            reasonRequired={false}
            confirmDisabled={!assignmentAllowed}
            buildCommand={() => ({
              operation: "POST /api/v1/admin/inquiries/{inquiryId}/assignment",
              params: { inquiryId: selected.id },
              body: undefined,
            })}
            onCommand={execute}
            onComplete={complete}
            onRefresh={async () => { await refreshLatest(); }}
          />
          <Dialog
            open={Boolean(pendingSelection)}
            onClose={() => setPendingSelection(undefined)}
            size="small"
            tone="danger"
            title="작성 중인 답변을 버릴까요?"
            description="다른 문의로 이동하면 현재 답변 초안은 복구할 수 없습니다."
            secondaryAction={<Button size="small" variant="quiet" onClick={() => setPendingSelection(undefined)}>계속 작성</Button>}
            primaryAction={(
              <Button
                size="small"
                variant="danger"
                onClick={() => {
                  const next = pendingSelection;
                  setPendingSelection(undefined);
                  replyForm.reset({ content: "" });
                  if (next) onSelect(next);
                }}
              >
                답변 버리고 이동
              </Button>
            )}
          >
            <p>현재 초안을 유지하려면 계속 작성을 선택해 주세요.</p>
          </Dialog>
          <Dialog
            open={Boolean(preview && previewFileId)}
            onClose={releasePreview}
            size="medium"
            title="문의 첨부 미리보기"
            description={`파일 ID ${previewFileId ?? "—"}`}
            primaryAction={<Button size="small" onClick={releasePreview}>미리보기 닫기</Button>}
          >
            {preview && previewFileId ? (
              <object data={preview.url} type={preview.contentType} width="100%" height="360" aria-label="문의 첨부 파일">
                <a href={preview.url} download={`문의첨부-${previewFileId}`}>첨부 파일 저장</a>
              </object>
            ) : null}
          </Dialog>
        </>
      ) : null}
      <UnsavedChangesGuard
        active={mode === "detail" && replyForm.formState.isDirty && Boolean(replyForm.getValues("content").trim())}
        description="작성 중인 문의 답변은 저장되지 않습니다."
      />
    </>
  );
}
